type RelayWebSocketConstructor = {
  debug?: boolean
  eventSourceUrl: string
  onMessage: (event: MessageEvent) => void
  channels: Array<string>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Logger = (...args: Array<any>) => void

const KEEP_ALIVE_TIME = 20_000
const MAX_RECONNECT_ATTEMPTS: number = 5
// 30s is too long of a duration and the socket will close. Juggernaut was 10s.
const RECONNECT_INTERVAL: number = 2_000

export class RelayWebSocket {
  private channels: string[]
  private debugLog: Logger
  private socket: WebSocket | null = null
  private eventSourceUrl: RelayWebSocketConstructor['eventSourceUrl']
  private keepAliveTimer: NodeJS.Timeout | undefined
  private onMessage: RelayWebSocketConstructor['onMessage']
  private reconnectAttempts: number = 0
  private reconnectTimer: NodeJS.Timeout | undefined

  constructor({
    eventSourceUrl,
    onMessage,
    debug = false,
    channels,
  }: RelayWebSocketConstructor) {
    this.debugLog = (...args) => {
      if (!debug) return
      console.log('[RelayWebSocket]', ...args)
    }
    this.eventSourceUrl = new URL(`/relay/socket/websocket`, eventSourceUrl)
      .toString()
      .replace('http', 'ws')
    this.channels = channels
    this.onMessage = onMessage
    this.connect()
  }

  closeHandler = (e: Event): void => {
    this.debugLog(`Connection closed for channels ${this.channels}`, e)
    this.handleReconnect()
  }

  errorHandler = (event: Event) => {
    this.debugLog(`Failed to connect for channels ${this.channels}`, event)
    this.socket?.close()
    clearTimeout(this.keepAliveTimer)
  }

  openHandler = (event: Event): void => {
    this.debugLog('EventSource opened', event)
    this.reconnectAttempts = 0
    // A single websocket can connect to multiple channels.
    this.channels.forEach((channelName) => {
      this.debugLog(`Joining channel ${channelName}`)
      const joinMessage = {
        topic: channelName,
        event: 'phx_join',
        payload: {},
        ref: null,
      }
      this.socket?.send(JSON.stringify(joinMessage))
    })
    this.keepAliveTimer = this.createKeepAliveTimer()
    clearTimeout(this.reconnectTimer)
  }

  createMessageHandler = (
    onMessage: RelayWebSocketConstructor['onMessage'],
    debugLog: Logger
  ): ((event: MessageEvent) => void) => {
    return (event: MessageEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let parsedData: any = {}
      try {
        parsedData = JSON.parse(event.data)

        if (
          parsedData.event === 'phx_reply' &&
          parsedData.topic === 'phoenix'
        ) {
          debugLog('Heartbeat event recieved, resetting keep-alive timer.')
          this.resetKeepAliveTimer()
          return
        }

        // If the event is not a data message, such as an ack on socket connect, ignore it.
        if (parsedData.event !== 'message') {
          debugLog('Received system message (non-data payload)', parsedData)
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        console.error('[RelayWebSocket] Unable to parse message.', e.message)
        return
      }

      if (parsedData.payload?.data) {
        // Some payloads are JSON:API formatted, others are not. The onMessage callback
        // should handle the payload accordingly, meaning the relevant payload will either be
        // event.data.payload.data.data (JSON:API) or event.data.payload.data (non-JSON:API)
        debugLog('Message data received', event, parsedData.payload)
        onMessage(parsedData.payload.data)
      } else {
        debugLog(
          'Message data received was not in known format',
          event,
          parsedData.payload
        )
      }
    }
  }

  handleReconnect = (): void => {
    if (this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      this.reconnectAttempts++
      const reconnectDelay = this.getReconnectDelay()
      this.reconnectTimer = setTimeout(() => {
        this.debugLog(
          `Reconnect attempt #${this.reconnectAttempts}, with delay ${reconnectDelay}ms`
        )
        this.connect()
      }, reconnectDelay)
    } else {
      this.debugLog('Connection is closed and reconnect attempts exhausted')
    }
  }

  connect = (): void => {
    this.socket = this.createEventSource()
    this.socket.onmessage = this.createMessageHandler(
      this.onMessage,
      this.debugLog
    )
    this.socket.onopen = this.openHandler
    this.socket.onerror = this.errorHandler
    this.socket.onclose = this.closeHandler
  }

  createEventSource = (): WebSocket => {
    this.debugLog(`Creating event source for ${this.eventSourceUrl}`)
    return new WebSocket(this.eventSourceUrl)
  }

  createKeepAliveTimer = (): NodeJS.Timeout => {
    return setTimeout(this.sendHeartbeat, KEEP_ALIVE_TIME)
  }

  getReconnectDelay = (): number => {
    return Math.min(
      RECONNECT_INTERVAL * Math.pow(2, this.reconnectAttempts - 1),
      60000
    )
  }

  resetKeepAliveTimer = (): void => {
    clearTimeout(this.keepAliveTimer)
    this.keepAliveTimer = this.createKeepAliveTimer()
  }

  sendHeartbeat = (): void => {
    // This socket will otherwise close after 30s of inactivity. Heartbeats are a built-in machination,
    // so there's no explicit implementation for heartbeats on the server.
    this.debugLog('Sending heartbeat for channels ', this.channels)
    if (this.socket?.readyState === WebSocket.OPEN) {
      const heartbeatMessage = {
        topic: 'phoenix',
        event: 'heartbeat',
        payload: {},
        ref: null,
      }
      this.socket?.send(JSON.stringify(heartbeatMessage))
    }
  }
}
